/**
 * Jack & Jones — product page (sections/jj-product.liquid).
 *
 * - Color and size: swatches + size boxes on desktop; on mobile a color select and a size dropdown
 *   with its own list (Figma 531:13663), kept in sync. The size starts unselected («SELECCIONAR TALLE»). The chosen variant goes to the form's input[name=id] and
 *   to the URL (?variant=), so sharing carries what is being looked at (rule E6).
 * - Photos: the ones of the chosen color. On desktop they scroll inside the frame (wheel) and the
 *   thumbnails follow, or a thumbnail moves them; on mobile the photos swipe and the bars below follow
 *   (a tap on a bar goes to its photo). Clicking a photo opens the zoom.
 * - Add to cart: posts /cart/add.js and announces it with Horizon's CartLinesUpdateEvent (its drawer,
 *   header bubble and cart page listen to it). The answer of the API is said on the button itself
 *   (rule A3). Without a size, a red «*Seleccioná tu talle para continuar.» shows over the sizes
 *   (desktop) or over the button (mobile), out of the flow so nothing moves (rule A5).
 * - Dialogs (zoom, size guide, stores): native <dialog>, Escape and a click outside close them and
 *   the focus goes back to what opened them. The size guide page, when it has a heading per category
 *   (TOPS, BOTTOMS…), gets a select on top that shows one category at a time.
 * - Store availability: Liquid only knows the stores of the selected variant, so the stores of the
 *   chosen color are asked for per size (section jj-product-stores-data) and drawn in the dialog.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {{ id: number, options: string[], available: boolean, low: boolean, price: string,
 *   compare: string, discount: number, net: string }} Variant
 * @typedef {{ id: number, name: string, address: string, available: boolean, pickUpTime: string }} StoreAvailability
 * @typedef {{ id: number, name: string, address: string, pickUpTime: string, variants: Variant[] }} Store
 */

class JJProduct {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    const data = JSON.parse(root.querySelector('[data-jj-pdp-data]')?.textContent || '{}');
    /** @type {Variant[]} */
    this.variants = data.variants || [];
    this.sizeIndex = Number(data.sizeIndex ?? -1);
    this.colorIndex = Number(data.colorIndex ?? -1);
    this.byColor = root.hasAttribute('data-media-by-color');

    this.form = /** @type {HTMLFormElement | null} */ (root.querySelector('[data-jj-form]'));
    this.idInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-jj-variant-input]'));
    this.addButton = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-jj-add]'));
    this.addLabel = root.querySelector('[data-jj-add-label]');
    this.status = root.querySelector('[data-jj-status]');
    this.track = /** @type {HTMLElement | null} */ (root.querySelector('[data-jj-track]'));
    this.dots = root.querySelector('[data-jj-dots]');
    this.zoom = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-jj-zoom]'));

    const checkedColor = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-jj-color-input]:checked'));
    const colorSelect = /** @type {HTMLSelectElement | null} */ (root.querySelector('[data-jj-color-select]'));
    this.color = checkedColor?.value ?? colorSelect?.value ?? '';
    const checkedSize = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-jj-size-input]:checked'));
    this.size = checkedSize?.value ?? '';

    /** @type {number | undefined} */
    this.labelTimer = undefined;
    /** Photo a thumbnail (or a bar) asked for: until it is reached, the scroll doesn't move the marks */
    this.target = '';
    this.busy = false;

    this.bind();
    this.buildGuideSelect();
    this.buildDots();
    this.render({ keepGallery: true });
  }

  /* ------------------------------------------------------------------ state */

  /** @returns {Variant | undefined} variant of the color with the size, or the only one without sizes */
  get chosen() {
    if (this.sizeIndex >= 0 && !this.size) return undefined;
    return this.variants.find((variant) => this.matches(variant, this.size));
  }

  /**
   * @param {Variant} variant
   * @param {string} size
   */
  matches(variant, size) {
    if (this.colorIndex >= 0 && variant.options[this.colorIndex] !== this.color) return false;
    if (this.sizeIndex >= 0 && variant.options[this.sizeIndex] !== size) return false;
    return true;
  }

  /** Variant whose price is shown: the chosen one, or the first (available) of the color */
  get shown() {
    return (
      this.chosen ||
      this.variants.find((v) => v.available && (this.colorIndex < 0 || v.options[this.colorIndex] === this.color)) ||
      this.variants.find((v) => this.colorIndex < 0 || v.options[this.colorIndex] === this.color) ||
      this.variants[0]
    );
  }

  /* ------------------------------------------------------------------ events */

  bind() {
    // Size guide loaded from its page: start on hover or focus of the link (desktop's and mobile's)
    this.root.querySelectorAll('.jj-pdp__size-guide').forEach((guideLink) => {
      ['pointerenter', 'focus'].forEach((type) => guideLink.addEventListener(type, () => this.loadGuide(), { once: true }));
    });

    // Stores of the color: asked for when the pointer or the focus reaches the sizes, so they are
    // usually there when a size is chosen and the link shows without a wait
    this.root.querySelectorAll('.jj-pdp__sizes, .jj-pdp__selects').forEach((pickers) => {
      ['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
        pickers.addEventListener(type, () => this.loadStores(this.color), { passive: true })
      );
    });

    const { root } = this;

    root.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (target.matches('[data-jj-color-input], [data-jj-color-select]')) {
        this.color = target.value;
        this.render();
      } else if (target.matches('[data-jj-size-input]')) {
        this.size = target.value;
        this.clearWarning();
        this.render({ keepGallery: true });
      }
    });

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const thumb = target.closest('[data-jj-thumb]');
      if (thumb instanceof HTMLElement) {
        this.goTo(thumb.dataset.mediaId || '');
        return;
      }

      // Mobile bars: a tap goes to that photo (Figma «Indicador» ON_CLICK), it doesn't open the zoom
      const dot = target.closest('[data-jj-dot]');
      if (dot instanceof HTMLElement) {
        const slide = this.visibleSlides[Number(dot.dataset.jjDot)];
        if (slide) this.goTo(slide.dataset.mediaId || '');
        return;
      }

      const zoomOpen = target.closest('[data-jj-zoom-open]');
      if (zoomOpen instanceof HTMLElement) {
        const slide = zoomOpen.closest('[data-jj-slide]');
        this.openZoom(zoomOpen, slide instanceof HTMLElement ? slide.dataset.mediaId || '' : '');
        return;
      }

      const dialogOpen = target.closest('[data-jj-dialog-open]');
      if (dialogOpen instanceof HTMLElement) {
        const dialog = document.getElementById(dialogOpen.dataset.jjDialogOpen || '');
        if (dialog instanceof HTMLDialogElement) this.openDialog(dialog, dialogOpen);
        return;
      }

      const step = target.closest('[data-jj-zoom-step]');
      if (step instanceof HTMLElement) {
        this.stepZoom(Number(step.dataset.jjZoomStep));
        return;
      }

      const close = target.closest('[data-jj-dialog-close]');
      if (close) close.closest('dialog')?.close();
    });

    this.root.querySelectorAll('dialog').forEach((dialog) => {
      // A click on the backdrop lands on the dialog itself
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside || dialog.classList.contains('jj-pdp-dialog--zoom')) dialog.close();
      });
      dialog.addEventListener('close', () => {
        unlockScroll(dialog);
        const trigger = this.dialogTriggers.get(dialog);
        if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({ preventScroll: true });
      });
    });

    // Zoom: the arrow keys move through the photos of the color, like the arrows on screen
    this.zoom?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.stepZoom(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });

    this.bindLens();
    this.bindSizeDropdown();

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.add();
    });

    if (this.track) {
      let frame = 0;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.syncDots());
        },
        { passive: true }
      );
      // The user takes over the scroll: the marks follow it again
      ['wheel', 'touchstart', 'pointerdown'].forEach((type) =>
        this.track?.addEventListener(type, () => (this.target = ''), { passive: true })
      );
    }
  }

  /**
   * Mobile size dropdown (Figma «Desplegable · selector» 1154:51229, open in 531:13663): the button
   * opens the list over the page with the focus on the chosen size (or the first); the arrows, Home and
   * End move, Enter or Space choose, Escape, the header or a tap outside close it, and the focus goes
   * back to the button.
   */
  bindSizeDropdown() {
    const dropdown = this.root.querySelector('[data-jj-size-dropdown]');
    const button = dropdown?.querySelector('[data-jj-size-button]');
    const panel = dropdown?.querySelector('[data-jj-size-panel]');
    if (!(dropdown instanceof HTMLElement) || !(button instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
    const options = () => /** @type {HTMLElement[]} */ (Array.from(panel.querySelectorAll('[data-jj-size-option]')));

    /** @param {PointerEvent} event */
    const onOutside = (event) => {
      if (!(event.target instanceof Node) || !dropdown.contains(event.target)) close(false);
    };
    const close = (focusButton = true) => {
      if (panel.hidden) return;
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', onOutside, true);
      if (focusButton) button.focus({ preventScroll: true });
    };
    const open = () => {
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', onOutside, true);
      const list = options();
      const current = list.find((option) => option.getAttribute('aria-selected') === 'true') || list[0];
      current?.focus({ preventScroll: true });
      current?.scrollIntoView({ block: 'nearest' });
    };
    /** @param {HTMLElement} option */
    const choose = (option) => {
      this.size = option.dataset.value || '';
      this.clearWarning();
      close();
      this.render({ keepGallery: true });
    };

    button.addEventListener('click', () => (panel.hidden ? open() : close()));
    button.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      open();
    });

    panel.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const option = target?.closest('[data-jj-size-option]');
      if (option instanceof HTMLElement) choose(option);
      else if (target?.closest('[data-jj-size-close]')) close();
    });

    panel.addEventListener('keydown', (event) => {
      const list = options();
      const index = list.findIndex((option) => option === document.activeElement);
      let next = -1;
      switch (event.key) {
        case 'ArrowDown':
          next = Math.min(index + 1, list.length - 1);
          break;
        case 'ArrowUp':
          next = Math.max(index - 1, 0);
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = list.length - 1;
          break;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const option = list[index];
          if (option) choose(option);
          return;
        }
        case 'Escape':
          event.preventDefault();
          close();
          return;
        case 'Tab':
          // The focus goes back to the button and Tab goes on from there
          close();
          return;
        default:
          return;
      }
      event.preventDefault();
      list[next]?.focus({ preventScroll: true });
      list[next]?.scrollIntoView({ block: 'nearest' });
    });
  }

  /** @type {WeakMap<HTMLDialogElement, Element>} */
  dialogTriggers = new WeakMap();

  /**
   * @param {HTMLDialogElement} dialog
   * @param {Element} trigger
   */
  openDialog(dialog, trigger) {
    this.dialogTriggers.set(dialog, trigger);
    dialog.showModal();
    lockScroll(dialog);
    if (dialog.classList.contains('jj-pdp-dialog--guide')) this.loadGuide();
  }

  /** @type {Promise<void> | undefined} */
  guideLoad = undefined;

  /**
   * Size guide drawn by a section of its page (jj-size-guide) instead of the page's body: the dialog
   * opens with «Cargando…» and takes the tables from the page itself ([data-jj-size-guide-body]).
   * A hidden copy of each group heading goes before its block so the category select splits the guide
   * by group; the heading itself stays visible on top of the tables and «¿Cómo medir?» goes under the
   * tables of every group (Figma 295:1098, 295:1099).
   * Hovering or focusing the link starts it, so it is usually there by the click.
   */
  loadGuide() {
    const body = this.root.querySelector('.jj-pdp-dialog--guide [data-jj-guide-src]');
    if (!(body instanceof HTMLElement) || this.guideLoad) return;
    this.guideLoad = fetch(body.dataset.jjGuideSrc || '', { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-jj-size-guide-body]');
        if (!source || !source.children.length) throw new Error('empty');
        const blocks = Array.from(source.children);
        blocks.forEach((block) => block.removeAttribute('data-shopify-editor-block'));
        const measures = blocks.filter((block) => block.classList.contains('jj-sg__block--measure'));
        const withMeasures = () => measures.map((block) => block.cloneNode(true));
        const nodes = [];
        blocks
          .filter((block) => !measures.includes(block))
          .forEach((block) => {
            const heading = block.querySelector('.jj-sg__group');
            if (heading) {
              if (nodes.length) nodes.push(...withMeasures());
              const marker = /** @type {HTMLElement} */ (heading.cloneNode(true));
              marker.classList.add('visually-hidden');
              marker.setAttribute('aria-hidden', 'true');
              nodes.push(marker);
            }
            nodes.push(block);
          });
        nodes.push(...withMeasures());
        body.replaceChildren(...nodes);
        body.removeAttribute('data-jj-guide-src');
        this.buildGuideSelect();
      })
      .catch(() => {
        // Reachable again on the next open; meanwhile a link to the page
        this.guideLoad = undefined;
        body.innerHTML = `<p class="jj-pdp-guide__status">${body.dataset.jjGuideError || ''}</p>`;
      });
  }

  /**
   * Size guide (Figma 296:5623): the page's content is split at its headings (the first level used
   * at the top of the page: h2, h3 or h4) and a select with their texts shows one part at a time.
   * With fewer than two headings the page shows as it is.
   */
  buildGuideSelect() {
    const body = this.root.querySelector('.jj-pdp-dialog--guide .jj-pdp-dialog__body');
    if (!(body instanceof HTMLElement)) return;
    const level = ['H2', 'H3', 'H4'].find((tag) => Array.from(body.children).some((child) => child.tagName === tag));
    if (!level) return;
    const headings = Array.from(body.children).filter((child) => child.tagName === level);
    if (headings.length < 2) return;

    // It opens on the part of the product's type (data-guide-group, from the section's setting; Figma
    // 295:1093: a T-shirt on the tops), else on the first one
    const plain = (/** @type {string | null | undefined} */ value) =>
      (value || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const group = plain(this.root.dataset.guideGroup);
    const start = Math.max(0, group ? headings.findIndex((heading) => plain(heading.textContent) === group) : 0);

    const select = document.createElement('select');
    select.className = 'jj-pdp-guide__select-native';
    select.setAttribute('aria-label', this.root.dataset.labelGuideCategory || '');
    const text = document.createElement('span');
    text.className = 'jj-pdp-guide__select-text';
    text.setAttribute('aria-hidden', 'true');

    const parts = headings.map((heading, i) => {
      const part = document.createElement('div');
      part.className = 'jj-pdp-guide__part';
      part.hidden = i !== start;
      heading.before(part);
      let node = heading.nextSibling;
      heading.classList.add('visually-hidden');
      part.append(heading);
      while (node && !(node instanceof Element && node.tagName === level)) {
        const next = node.nextSibling;
        part.append(node);
        node = next;
      }
      const option = document.createElement('option');
      option.value = String(i);
      option.textContent = heading.textContent?.trim() || '';
      select.append(option);
      return part;
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'jj-pdp-guide__select';
    wrapper.innerHTML =
      '<svg class="jj-icon jj-pdp-guide__select-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="m3.5 7.8 8.5 8.5 8.5-8.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    wrapper.prepend(text, select);
    body.prepend(wrapper);

    const show = () => {
      parts.forEach((part, i) => (part.hidden = String(i) !== select.value));
      text.textContent = select.selectedOptions[0]?.textContent || '';
    };
    select.value = String(start);
    select.addEventListener('change', show);
    show();
  }

  /* ------------------------------------------------------------------ render */

  /** @param {{ keepGallery?: boolean }} [options] */
  render({ keepGallery = false } = {}) {
    const { root } = this;

    // Color: name, swatches and select
    root.querySelectorAll('[data-jj-color-name]').forEach((name) => (name.textContent = this.color));
    root.querySelectorAll('[data-jj-color-input]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = input.value === this.color;
    });
    const colorSelect = root.querySelector('[data-jj-color-select]');
    if (colorSelect instanceof HTMLSelectElement && this.color) colorSelect.value = this.color;

    // A size that doesn't exist in the new color is dropped
    if (this.size && this.sizeIndex >= 0 && !this.variants.some((v) => this.matches(v, this.size))) this.size = '';

    // Sizes: sold out per color, checked state
    root.querySelectorAll('[data-jj-size-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const variant = this.variants.find((v) => this.matches(v, input.value));
      const soldOut = !variant || !variant.available;
      input.checked = input.value === this.size;
      const label = input.closest('[data-jj-size-label]');
      label?.classList.toggle('is-sold-out', soldOut);
      const hint = label?.querySelector('[data-jj-size-sold-out]');
      if (hint instanceof HTMLElement) hint.hidden = !soldOut;
    });
    // Mobile dropdown: the chosen size and the sold out ones of the color, like the boxes
    root.querySelectorAll('[data-jj-size-option]').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      const value = option.dataset.value || '';
      const variant = this.variants.find((v) => this.matches(v, value));
      const soldOut = !variant || !variant.available;
      option.setAttribute('aria-selected', String(value === this.size));
      option.classList.toggle('is-sold-out', soldOut);
      const hint = option.querySelector('[data-jj-size-sold-out]');
      if (hint instanceof HTMLElement) hint.hidden = !soldOut;
    });
    const sizeText = root.querySelector('[data-jj-size-text]');
    if (sizeText instanceof HTMLElement) sizeText.textContent = this.size || sizeText.dataset.placeholder || '';

    // Price of the chosen variant (or the color's first)
    const shown = this.shown;
    if (shown) {
      const compare = root.querySelector('[data-jj-compare]');
      const compareValue = root.querySelector('[data-jj-compare-value]');
      const current = root.querySelector('[data-jj-current]');
      const discount = root.querySelector('[data-jj-discount]');
      const net = root.querySelector('[data-jj-net]');
      if (compare instanceof HTMLElement) compare.hidden = !shown.compare;
      if (compareValue) compareValue.textContent = shown.compare;
      if (current) current.textContent = shown.price;
      if (discount instanceof HTMLElement) {
        discount.hidden = !(shown.discount > 0);
        discount.textContent = `-${shown.discount}%`;
      }
      if (net) net.textContent = shown.net;
    }

    // Form, URL, button, stores
    const chosen = this.chosen;
    if (this.idInput) this.idInput.value = chosen ? String(chosen.id) : '';
    this.syncUrl(chosen);
    this.syncButton();
    this.syncStores();

    if (!keepGallery) this.filterMedia();
  }

  /** @param {Variant | undefined} chosen */
  syncUrl(chosen) {
    const url = new URL(window.location.href);
    if (chosen) url.searchParams.set('variant', String(chosen.id));
    else url.searchParams.delete('variant');
    if (url.href !== window.location.href) history.replaceState(history.state, '', url.toString());
  }

  syncButton() {
    const button = this.addButton;
    if (!button || this.busy) return;
    const { dataset } = this.root;
    const chosen = this.chosen;
    const anyAvailable = this.variants.some((v) => v.available);

    let label = dataset.labelAdd || '';
    let disabled = false;
    if (!anyAvailable) {
      label = dataset.labelSoldOutButton || '';
      disabled = true;
    } else if (!chosen) {
      label = dataset.labelSelect || '';
    } else if (!chosen.available) {
      label = dataset.labelSoldOutButton || '';
      disabled = true;
    }
    window.clearTimeout(this.labelTimer);
    button.disabled = disabled;
    if (this.addLabel) this.addLabel.textContent = label;
  }

  /**
   * Says something on the button and goes back to its state after `duration` ms.
   * @param {string} text
   * @param {number} duration
   */
  flashLabel(text, duration) {
    if (this.addLabel) this.addLabel.textContent = text;
    if (this.status) this.status.textContent = text;
    window.clearTimeout(this.labelTimer);
    this.labelTimer = window.setTimeout(() => {
      this.clearWarning();
      this.syncButton();
    }, duration);
  }

  clearWarning() {
    this.root.querySelectorAll('[data-jj-size-message]').forEach((message) => {
      if (message instanceof HTMLElement) message.hidden = true;
    });
  }

  /* ------------------------------------------------------------------ stores */

  /** @type {Map<string, Promise<Store[]>>} */
  storeLoads = new Map();

  /**
   * Stores that have some size of a color in stock, in the order Shopify gives them, each with those
   * sizes (Figma 296:5759: a store shows all its sizes and is listed even without the chosen one).
   * One request per size of the color to the jj-product-stores-data section, kept for the visit; a
   * failed load is asked for again next time.
   * @param {string} color
   * @returns {Promise<Store[]>}
   */
  loadStores(color) {
    const cached = this.storeLoads.get(color);
    if (cached) return cached;
    const url = this.root.dataset.productUrl || window.location.pathname;
    const variants = this.variants.filter((v) => this.colorIndex < 0 || v.options[this.colorIndex] === color);
    const load = Promise.all(
      variants.map((variant) => {
        const params = new URLSearchParams({ variant: String(variant.id), section_id: 'jj-product-stores-data' });
        return fetch(`${url}?${params}`, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(String(response.status));
            return response.text();
          })
          .then((html) => {
            const script = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-jj-stores-data]');
            const data = JSON.parse(script?.textContent || '{}');
            /** @type {StoreAvailability[]} */
            const stores = Array.isArray(data.stores) ? data.stores : [];
            return { variant, stores };
          });
      })
    )
      .then((results) => {
        /** @type {Map<number, Store>} */
        const byLocation = new Map();
        results.forEach(({ variant, stores }) => {
          stores.forEach((store) => {
            let entry = byLocation.get(store.id);
            if (!entry) {
              entry = { id: store.id, name: store.name, address: store.address, pickUpTime: '', variants: [] };
              byLocation.set(store.id, entry);
            }
            if (!store.available) return;
            entry.variants.push(variant);
            if (!entry.pickUpTime) entry.pickUpTime = store.pickUpTime || '';
          });
        });
        return Array.from(byLocation.values()).filter((store) => store.variants.length > 0);
      })
      .catch((error) => {
        this.storeLoads.delete(color);
        console.warn('[jj-product] stores', error);
        return /** @type {Store[]} */ ([]);
      });
    this.storeLoads.set(color, load);
    return load;
  }

  /** The link shows once a size is chosen and some store has the color; the list follows the choice */
  syncStores() {
    const link = this.root.querySelector('[data-jj-stores-link]');
    if (!(link instanceof HTMLElement)) return;
    if (!this.chosen) {
      link.hidden = true;
      return;
    }
    const color = this.color;
    this.loadStores(color).then((stores) => {
      if (color !== this.color) return;
      const chosen = this.chosen;
      link.hidden = !chosen || stores.length === 0;
      this.drawStores(stores, chosen);
    });
  }

  /**
   * Store rows (Figma 296:5759): name, address in one line, and the sizes in stock as chips, the
   * chosen one marked; «últimas unidades» in yellow
   * @param {Store[]} stores
   * @param {Variant | undefined} chosen
   */
  drawStores(stores, chosen) {
    const dialog = this.root.querySelector('.jj-pdp-dialog--stores');
    if (!(dialog instanceof HTMLElement)) return;
    const list = dialog.querySelector('[data-jj-stores-list]');
    const status = dialog.querySelector('[data-jj-stores-status]');
    const { labelAvailable = '', labelSizes = '', labelNone = '' } = dialog.dataset;

    const paragraph = (/** @type {string} */ className, /** @type {string} */ text) => {
      const p = document.createElement('p');
      p.className = className;
      p.textContent = text;
      return p;
    };

    const items = stores.map((store) => {
      const item = document.createElement('li');
      item.className = 'jj-pdp-stores__item';
      item.append(paragraph('jj-pdp-stores__name', store.name));
      if (store.address) item.append(paragraph('jj-pdp-stores__address', store.address));
      // The state stays for screen readers; on screen the chips say it
      const state = [labelAvailable, store.pickUpTime].filter(Boolean).join(' · ');
      item.append(paragraph('jj-pdp-stores__state visually-hidden', state));
      if (this.sizeIndex >= 0) {
        const sizes = document.createElement('ul');
        sizes.className = 'jj-pdp-stores__sizes';
        sizes.setAttribute('role', 'list');
        sizes.setAttribute('aria-label', labelSizes);
        store.variants.forEach((variant) => {
          const chip = document.createElement('li');
          chip.className = 'jj-pdp-stores__size';
          chip.classList.toggle('is-low', variant.low);
          if (chosen && variant.id === chosen.id) {
            chip.classList.add('is-current');
            chip.setAttribute('aria-current', 'true');
          }
          chip.textContent = variant.options[this.sizeIndex] || '';
          sizes.append(chip);
        });
        item.append(sizes);
      }
      return item;
    });

    if (list instanceof HTMLElement) {
      list.replaceChildren(...items);
      list.hidden = items.length === 0;
    }
    if (status instanceof HTMLElement) {
      status.hidden = items.length > 0;
      status.textContent = items.length ? '' : labelNone;
    }
  }

  /* ------------------------------------------------------------------ gallery */

  get visibleSlides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.root.querySelectorAll('[data-jj-slide]:not([hidden])')));
  }

  filterMedia() {
    if (!this.byColor) return;
    const show = (/** @type {Element} */ element) => {
      if (!(element instanceof HTMLElement)) return;
      const color = element.dataset.jjColor || '';
      element.hidden = color !== '' && color !== this.color;
    };
    this.root.querySelectorAll('[data-jj-slide], [data-jj-thumb], [data-jj-zoom-item]').forEach(show);
    const first = this.visibleSlides[0];
    this.buildDots();
    if (first) this.goTo(first.dataset.mediaId || '', 'instant');
    const thumbs = this.root.querySelector('[data-jj-thumbs]');
    if (thumbs) thumbs.scrollTop = 0;
  }

  /**
   * Shows a photo: scrolls the track (vertical on desktop, horizontal on mobile).
   * @param {string} mediaId
   * @param {ScrollBehavior} [behavior]
   */
  goTo(mediaId, behavior = 'smooth') {
    const track = this.track;
    const slide = this.visibleSlides.find((s) => s.dataset.mediaId === mediaId);
    if (!track || !slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.target = mediaId;
    track.scrollTo({
      top: slide.offsetTop - track.offsetTop,
      left: slide.offsetLeft - track.offsetLeft,
      behavior: reduce ? 'instant' : behavior,
    });
    this.setCurrent(mediaId);
  }

  /** @param {string} mediaId */
  setCurrent(mediaId) {
    this.root.querySelectorAll('[data-jj-thumb]').forEach((thumb) => {
      if (!(thumb instanceof HTMLElement)) return;
      if (thumb.dataset.mediaId === mediaId) {
        thumb.setAttribute('aria-current', 'true');
        const list = thumb.parentElement;
        if (list && (thumb.offsetTop < list.scrollTop || thumb.offsetTop + thumb.offsetHeight > list.scrollTop + list.clientHeight)) {
          list.scrollTo({ top: thumb.offsetTop - list.offsetTop, behavior: 'smooth' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    const index = this.visibleSlides.findIndex((s) => s.dataset.mediaId === mediaId);
    this.dots?.querySelectorAll('.jj-pdp__dot').forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  buildDots() {
    if (!this.dots) return;
    const slides = this.visibleSlides;
    this.dots.replaceChildren(
      ...(slides.length > 1
        ? slides.map((_, i) => {
            // Pointer only: the list is aria-hidden and the photos are reached by swiping
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.tabIndex = -1;
            dot.className = 'jj-pdp__dot';
            dot.dataset.jjDot = String(i);
            dot.setAttribute('aria-label', (this.dots.dataset.jjDotLabel || '').replace('[n]', String(i + 1)));
            if (i === 0) dot.setAttribute('aria-current', 'true');
            return dot;
          })
        : [])
    );
  }

  /** Swipe (mobile) or wheel (desktop): the bar and the thumbnail of the photo that fills the frame */
  syncDots() {
    const track = this.track;
    if (!track) return;
    if (track.scrollLeft === 0 && track.scrollTop === 0) {
      const first = this.visibleSlides[0];
      this.target = '';
      if (first) this.setCurrent(first.dataset.mediaId || '');
      return;
    }
    const slides = this.visibleSlides;
    const horizontal = track.scrollWidth > track.clientWidth + 1;
    const position = horizontal ? track.scrollLeft : track.scrollTop;
    const current = slides.reduce((best, slide) => {
      const offset = horizontal ? slide.offsetLeft - track.offsetLeft : slide.offsetTop - track.offsetTop;
      const bestOffset = horizontal ? best.offsetLeft - track.offsetLeft : best.offsetTop - track.offsetTop;
      return Math.abs(offset - position) < Math.abs(bestOffset - position) ? slide : best;
    }, slides[0]);
    if (!current) return;
    const id = current.dataset.mediaId || '';
    if (this.target) {
      if (id === this.target) this.target = '';
      return;
    }
    this.setCurrent(id);
  }

  /**
   * @param {Element} trigger
   * @param {string} mediaId
   */
  openZoom(trigger, mediaId) {
    const zoom = this.zoom;
    if (!zoom) return;
    const item = zoom.querySelector(`[data-jj-zoom-item][data-media-id="${CSS.escape(mediaId)}"]`);
    if (item instanceof HTMLElement) this.showZoomItem(item);
    this.openDialog(zoom, trigger);
  }

  /**
   * The zoom shows one photo at a time.
   * @param {HTMLElement} item
   */
  showZoomItem(item) {
    this.zoom?.querySelectorAll('[data-jj-zoom-item]').forEach((other) => other.classList.toggle('is-current', other === item));
    const image = item.querySelector('img');
    if (image) image.loading = 'eager';
    // With a single photo there is nowhere to go
    const single = this.visibleZoomItems.length < 2;
    this.zoom?.querySelectorAll('[data-jj-zoom-step]').forEach((arrow) => ((/** @type {HTMLElement} */ (arrow)).hidden = single));
  }

  /**
   * Desktop «Lupa»: while the pointer is over the zoomed photo, a 300×300 square follows it with the
   * photo at 3×, centered on the same point. Only with a real hover (mouse), not on touch.
   */
  bindLens() {
    const stage = this.zoom?.querySelector('.jj-pdp-zoom__stage');
    const lens = /** @type {HTMLElement | null | undefined} */ (this.zoom?.querySelector('[data-jj-zoom-lens]'));
    if (!(stage instanceof HTMLElement) || !lens) return;
    const SCALE = 3;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');

    stage.addEventListener('pointermove', (event) => {
      const item = stage.querySelector('[data-jj-zoom-item].is-current');
      const onArrowOrClose = event.target instanceof Element && event.target.closest('button');
      if (!canHover.matches || event.pointerType !== 'mouse' || !(item instanceof HTMLElement) || onArrowOrClose) {
        lens.hidden = true;
        return;
      }
      const rect = stage.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const src = item.dataset.jjLensSrc || '';
      if (lens.dataset.src !== src) {
        lens.dataset.src = src;
        lens.style.backgroundImage = `url("${src}")`;
      }
      lens.hidden = false;
      // The box is centered with its border; the background starts inside the border
      const half = lens.offsetWidth / 2;
      const inner = lens.clientWidth / 2;
      lens.style.backgroundSize = `${rect.width * SCALE}px ${rect.height * SCALE}px`;
      lens.style.backgroundPosition = `${inner - x * SCALE}px ${inner - y * SCALE}px`;
      lens.style.transform = `translate(${x - half}px, ${y - half}px)`;
    });
    stage.addEventListener('pointerleave', () => (lens.hidden = true));
    this.zoom?.addEventListener('close', () => (lens.hidden = true));
  }

  get visibleZoomItems() {
    return /** @type {HTMLElement[]} */ (Array.from(this.zoom?.querySelectorAll('[data-jj-zoom-item]:not([hidden])') ?? []));
  }

  /**
   * Previous (-1) or next (1) photo of the zoom, wrapping around.
   * @param {number} step
   */
  stepZoom(step) {
    const items = this.visibleZoomItems;
    if (items.length < 2) return;
    const index = items.findIndex((item) => item.classList.contains('is-current'));
    this.showZoomItem(items[(index + step + items.length) % items.length]);
  }

  /* ------------------------------------------------------------------ cart */

  async add() {
    if (this.busy || !this.addButton || this.addButton.disabled) return;
    const chosen = this.chosen;
    const { dataset } = this.root;

    if (!chosen) {
      // Red message over the sizes (Figma 550:15272); the button keeps «SELECCIONAR TALLE»
      this.root.querySelectorAll('[data-jj-size-message]').forEach((message) => {
        if (message instanceof HTMLElement) message.hidden = false;
      });
      if (this.status) this.status.textContent = dataset.labelChoose || '';
      // The size control that is on screen: the boxes on desktop, the dropdown on mobile
      const controls = /** @type {HTMLElement[]} */ (
        Array.from(this.root.querySelectorAll('[data-jj-size-input], [data-jj-size-button]'))
      );
      const control = controls.find((control) => control.getClientRects().length > 0);
      if (control?.hasAttribute('data-jj-size-button')) {
        // A tap brought the focus here: no ring on the dropdown (Figma 531:13584) until a key is used
        control.setAttribute('data-jj-quiet-focus', '');
        const unquiet = () => control.removeAttribute('data-jj-quiet-focus');
        control.addEventListener('keydown', unquiet, { once: true });
        control.addEventListener('blur', unquiet, { once: true });
      }
      control?.focus({ preventScroll: true, focusVisible: false });
      return;
    }

    this.busy = true;
    const button = this.addButton;
    button.setAttribute('aria-busy', 'true');
    if (this.addLabel) this.addLabel.textContent = dataset.labelAdding || '';

    /** @type {any} */
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      button.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(chosen.id), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      deferred = null;
    }

    const routes = /** @type {any} */ (window).Theme?.routes || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = `${routes.cart_url || '/cart'}.js`;
    let text = '';
    let duration = 2500;
    let ok = false;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: chosen.id, quantity: 1 }] }),
      });
      const data = await response.json();
      ok = response.ok;
      if (ok) {
        text = dataset.labelAdded || '';
      } else {
        // «Sólo hay 2 unidades», «agotado»…: what the API says, where it was asked (rule A3)
        text = data.description || data.message || dataset.labelError || '';
        duration = 4000;
      }
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      try {
        deferred?.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, source: 'jj-product', didError: !ok },
        });
      } catch (error) {
        console.warn('[jj-product] cart event', error);
      }
    } catch (error) {
      deferred?.reject(error);
      text = dataset.labelError || '';
      duration = 4000;
    } finally {
      this.busy = false;
      button.removeAttribute('aria-busy');
    }

    if (ok && dataset.cartType === 'page') {
      window.location.href = routes.cart_url || '/cart';
      return;
    }
    this.flashLabel(text, duration);
  }
}

function init() {
  document.querySelectorAll('[data-jj-pdp]').forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.jjReady) return;
    root.dataset.jjReady = 'true';
    new JJProduct(root);
  });
}

init();
document.addEventListener('shopify:section:load', init);
